import PropTypes from 'prop-types';
import IncidentCard from '../IncidentCard';
import { calculateRiskScore, calculateTrend } from '@/utils/riskScoring';

/**
 * DLP Incidents View Component for SOC Dashboard
 * Displays Data Loss Prevention incidents with detailed breakdown
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - DLP incident data
 * @param {Object} props.previousData - Previous period data for trend calculation
 */
const DLPIncidentsView = ({ data, previousData }) => {
  if (!data) {
    return (
      <div className="soc-view dlp-incidents-view">
        <h2 className="view-title">Data Loss Prevention (DLP) Incidents</h2>
        <p className="no-data-message">Loading DLP data...</p>
      </div>
    );
  }

  // Calculate risk scores for each DLP category
  const fileExfiltrationScore = calculateRiskScore({
    fileCount: data.dlpFileExfiltrationCount || 0,
    dataVolumeMB: data.dlpFileExfiltrationVolume || 0
  });

  const unusualSharingScore = calculateRiskScore({
    fileCount: data.dlpUnusualSharingCount || 0,
    uniqueIPCount: data.dlpUnusualSharingUniqueIPs || 0
  });

  const largeTransfersScore = calculateRiskScore({
    fileCount: data.dlpLargeTransfersCount || 0,
    dataVolumeMB: data.dlpLargeTransfersVolume || 0,
    uniqueIPCount: data.dlpLargeTransfersUniqueIPs || 0
  });

  // Calculate trends
  const fileExfiltrationTrend = previousData ? calculateTrend(
    data.dlpFileExfiltrationCount || 0,
    previousData.dlpFileExfiltrationCount || 0
  ) : null;

  const unusualSharingTrend = previousData ? calculateTrend(
    data.dlpUnusualSharingCount || 0,
    previousData.dlpUnusualSharingCount || 0
  ) : null;

  const largeTransfersTrend = previousData ? calculateTrend(
    data.dlpLargeTransfersCount || 0,
    previousData.dlpLargeTransfersCount || 0
  ) : null;

  const totalDLPIncidents = (data.dlpFileExfiltrationCount || 0) + 
                           (data.dlpUnusualSharingCount || 0) + 
                           (data.dlpLargeTransfersCount || 0);

  return (
    <div className="soc-view dlp-incidents-view">
      <h2 className="view-title">Data Loss Prevention (DLP)</h2>

      {/* Summary Header */}
      <div className="view-summary-header">
        <div className="summary-stat">
          <span className="summary-label">Total DLP Incidents</span>
          <span className="summary-value">{totalDLPIncidents}</span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Data at Risk</span>
          <span className="summary-value">
            {Math.round((data.dlpTotalDataVolume || 0) / 1024)} GB
          </span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Affected Users</span>
          <span className="summary-value">{data.dlpAffectedUsers || 0}</span>
        </div>
      </div>

      {/* DLP Incident Cards */}
      <div className="incident-cards-grid">
        <IncidentCard
          title="File Exfiltration Detection"
          riskScore={fileExfiltrationScore}
          data={{
            count: data.dlpFileExfiltrationCount || 0,
            dataVolume: `${Math.round((data.dlpFileExfiltrationVolume || 0) / 1024)} GB`,
            affectedDevices: data.dlpFileExfiltrationDevices || 0,
            topFileTypes: data.dlpFileExfiltrationTopTypes?.join(', ') || 'N/A'
          }}
          incidentType="DLP"
          trend={fileExfiltrationTrend}
          expandable={true}
        />

        <IncidentCard
          title="Unusual Sharing Patterns"
          riskScore={unusualSharingScore}
          data={{
            count: data.dlpUnusualSharingCount || 0,
            externalShares: data.dlpUnusualSharingExternal || 0,
            anonymousLinks: data.dlpUnusualSharingAnonymous || 0,
            uniqueIPs: data.dlpUnusualSharingUniqueIPs || 0
          }}
          incidentType="DLP"
          trend={unusualSharingTrend}
          expandable={true}
        />

        <IncidentCard
          title="Large Data Transfers"
          riskScore={largeTransfersScore}
          data={{
            count: data.dlpLargeTransfersCount || 0,
            totalVolume: `${Math.round((data.dlpLargeTransfersVolume || 0) / 1024)} GB`,
            uniqueDestinations: data.dlpLargeTransfersUniqueIPs || 0,
            peakTransferTime: data.dlpLargeTransfersPeakTime || 'N/A'
          }}
          incidentType="DLP"
          trend={largeTransfersTrend}
          expandable={true}
        />
      </div>

      {/* Detailed Info Section */}
      <div className="view-details-section">
        <h3>DLP Policy Status</h3>
        <div className="policy-status-grid">
          <div className="policy-item">
            <span className="policy-label">Active Policies</span>
            <span className="policy-value">{data.dlpActivePolicies || 0}</span>
          </div>
          <div className="policy-item">
            <span className="policy-label">Policy Violations</span>
            <span className="policy-value violations">{data.dlpPolicyViolations || 0}</span>
          </div>
          <div className="policy-item">
            <span className="policy-label">Quarantined Files</span>
            <span className="policy-value">{data.dlpQuarantinedFiles || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

DLPIncidentsView.propTypes = {
  data: PropTypes.object,
  previousData: PropTypes.object
};

DLPIncidentsView.defaultProps = {
  data: null,
  previousData: null
};

export default DLPIncidentsView;
